import {
  ChargingEnergyResponsibility,
  CompanyCreditProfile,
  CreditSupportUnit,
  DispatchRights,
  ISO,
  MeasurementBasis,
  PRODUCT_TYPE,
  PriceUnit,
  ProductScope,
  SettlementMethod,
  Side,
  TECHNOLOGY_TYPE,
  TbVariant,
} from '@/utils/enums';

import { baseApi } from './baseApi';
import { BidDTO } from './bidApi';
import { OfferDTO } from './offerApi';
import { ApiTags, CommonTagIds, MarketDepthTagIds } from './tags';
import { MatchingCriteriaFlags } from './types';

export type MarketDepthDto = {
  sideId: string;
  sideName: string;
  uniqueId: number;
  statusId: string;
  statusName: string;
  price: number;
  priceUnit?: PriceUnit | null;
  isoId: string;
  isoName: string;
  /** The hub. A nodal row's hub is the hub it is measured against. */
  settlementPoint: string;
  /** `NODAL` or `HUB`; null on a product that carries none, which counts as Hub. */
  settlementMethodId?: SettlementMethod | null;
  /** An unmasked nodal offer's node. Null on every masked row and on every bid. */
  settlementNode?: string | null;
  startDate: string;
  targetCod: string;
  tenorMonths: number;
  technologyTypeId: string;
  technologyTypeName: string;
  productTypeId: string;
  productTypeName: string;
  contractTypeId: string;
  contractTypeName: string;
  recProgramId: string;
  recProgramName: string;
  creditProfileId: string;
  creditProfileName: string;
  creditDescription: string;
  creditSupportUnit?: CreditSupportUnit | null;
  companyId: string;
  volumeMwh?: number;
  buyerPostCodCollateral?: number;
  /** A BESS bid's Contracted Capacity. Always null on an offer row. */
  requestedCapacityMw?: number | null;
  expectedSettlementPrice?: number;
  p50ForecastVolume?: number;
  impliedCarbonPrice?: number;
  offeredCapacityMw?: number;
  sellerPostCodCollateral?: number;
  projectId?: string;
  projectName?: string;
  cod?: string;
  matching247?: boolean;
  basisSharing?: boolean;
  bessOption?: boolean;
  capacityRights?: boolean;
  colocationOption?: boolean;
  escalator?: boolean;
  feocCompliant?: boolean;
  financingPartyConsentRequired?: boolean;
  fullDeliverability?: boolean;
  itcSafeHarbored?: boolean;
  nodalSettlement?: boolean;
  priceCap?: boolean;
  priceCollar?: boolean;
  priceFloor?: boolean;
  realTimeDataAccess?: boolean;
  ssa?: boolean;
  suretyBond?: boolean;
  taxCreditTransferability?: boolean;
  upfrontPayment?: boolean;
  upsideRevenueShare?: boolean;
  tbVariantId?: string | null;
  tbVariantName?: string | null;
  durationHours?: number | null;
  /** A Floor's Dispatch Rights. Null on every other product. */
  dispatchRightsId?: DispatchRights | null;
  dispatchRightsName?: string | null;
  /** The six Product Scope streams. All false or null on a product that carries none. */
  scopeEnergy?: boolean | null;
  scopeCapacity?: boolean | null;
  scopeAncillary?: boolean | null;
  scopeTransmission?: boolean | null;
  scopeDemandResponse?: boolean | null;
  scopeBlackstart?: boolean | null;
  /** Toll and Floor. Null on every other product. */
  chargingEnergyResponsibilityId?: ChargingEnergyResponsibility | null;
  chargingEnergyResponsibilityName?: string | null;
  /** Floor. Null on every other product. */
  measurementBasisId?: MeasurementBasis | null;
  measurementBasisName?: string | null;
  sharingThreshold?: number | null;
  sharingThresholdUnit?: PriceUnit | null;
  sharePercentage?: number | null;
  variableOmIncluded?: boolean | null;
  variableOmRate?: number | null;
  variableOmRateUnit?: PriceUnit | null;
} & MatchingCriteriaFlags;

export type GetMarketDepthParams = {
  sideView: Side;
  companyId: string;
  isos?: ISO[];
  technologies?: TECHNOLOGY_TYPE[];
  productTypes?: PRODUCT_TYPE[];
  contractTypes?: string[];
  applicableRecPrograms?: string[];
  hubZoneMatrixIds?: number[];
  creditProfiles?: CompanyCreditProfile[];
  fixedPriceMin?: number;
  fixedPriceMax?: number;
  tenorStartDateMin?: string;
  tenorStartDateMax?: string;
  tenorMonthsMin?: number;
  tenorMonthsMax?: number;
  /** The unit of the listed prices. The backend lists $/MWh when it is absent. */
  priceUnit?: PriceUnit;
  tbVariants?: TbVariant[];
  settlementMethods?: SettlementMethod[];
  productScopes?: ProductScope[];
  dispatchRights?: DispatchRights[];
  measurementBases?: MeasurementBasis[];
};

const appendAll = (
  searchParams: URLSearchParams,
  name: string,
  values: readonly string[] | undefined
) => values?.forEach(value => searchParams.append(name, value));

const BASE_URL = '/api/v1/market-depth';

export const marketDepthApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getMarketDepth: build.query<MarketDepthDto[], GetMarketDepthParams>({
      query: ({
        sideView,
        companyId,
        isos,
        technologies,
        productTypes,
        contractTypes,
        applicableRecPrograms,
        hubZoneMatrixIds,
        creditProfiles,
        fixedPriceMin,
        fixedPriceMax,
        tenorStartDateMin,
        tenorStartDateMax,
        tenorMonthsMin,
        tenorMonthsMax,
        priceUnit,
        tbVariants,
        settlementMethods,
        productScopes,
        dispatchRights,
        measurementBases,
      }) => {
        const searchParams = new URLSearchParams();

        if (isos) {
          isos.forEach(iso => searchParams.append('isos', iso));
        }
        if (technologies) {
          technologies.forEach(tech =>
            searchParams.append('technologies', tech)
          );
        }
        if (productTypes) {
          productTypes.forEach(type =>
            searchParams.append('productTypes', type)
          );
        }
        if (contractTypes) {
          contractTypes.forEach(type =>
            searchParams.append('contractTypes', type)
          );
        }
        if (applicableRecPrograms) {
          applicableRecPrograms.forEach(program =>
            searchParams.append('applicableRecPrograms', program)
          );
        }
        if (hubZoneMatrixIds) {
          hubZoneMatrixIds.forEach(id =>
            searchParams.append('hubZoneMatrixIds', id.toString())
          );
        }
        if (creditProfiles) {
          creditProfiles.forEach(profile =>
            searchParams.append('creditProfiles', profile)
          );
        }
        if (fixedPriceMin !== undefined) {
          searchParams.append('fixedPriceMin', fixedPriceMin.toString());
        }
        if (fixedPriceMax !== undefined) {
          searchParams.append('fixedPriceMax', fixedPriceMax.toString());
        }
        if (tenorStartDateMin) {
          searchParams.append('tenorStartDateMin', tenorStartDateMin);
        }
        if (tenorStartDateMax) {
          searchParams.append('tenorStartDateMax', tenorStartDateMax);
        }
        if (tenorMonthsMin !== undefined) {
          searchParams.append('tenorMonthsMin', tenorMonthsMin.toString());
        }
        if (tenorMonthsMax !== undefined) {
          searchParams.append('tenorMonthsMax', tenorMonthsMax.toString());
        }
        if (priceUnit) {
          searchParams.append('priceUnit', priceUnit);
        }
        appendAll(searchParams, 'tbVariants', tbVariants);
        appendAll(searchParams, 'settlementMethods', settlementMethods);
        appendAll(searchParams, 'productScopes', productScopes);
        appendAll(searchParams, 'dispatchRights', dispatchRights);
        appendAll(searchParams, 'measurementBases', measurementBases);

        const queryString = searchParams.toString();
        const suffix = queryString ? `?${queryString}` : '';
        const url = `${BASE_URL}${suffix}`;

        return {
          url,
          headers: {
            'X-Side-View': sideView,
            'X-Company-Id': companyId,
          },
        };
      },
      providesTags: (result, error, { companyId, sideView }) => [
        {
          type: ApiTags.MARKET_DEPTH,
          id: `${CommonTagIds.LIST}-${companyId}-${sideView}`,
        },
      ],
    }),
    getMatchingBids: build.query<
      BidDTO[],
      { offerId: number; companyId: string }
    >({
      query: ({ offerId, companyId }) => ({
        url: `${BASE_URL}/matching-bids?offerId=${offerId}`,
        headers: {
          'X-Company-Id': companyId,
        },
      }),
      providesTags: (result, error, { offerId }) => [
        {
          type: ApiTags.MARKET_DEPTH,
          id: MarketDepthTagIds.MATCHING_BIDS,
        },
        {
          type: ApiTags.MARKET_DEPTH,
          id: `${MarketDepthTagIds.MATCHING_BIDS}-${offerId}`,
        },
      ],
    }),
    getMatchingOffers: build.query<
      OfferDTO[],
      { bidId: number; companyId: string }
    >({
      query: ({ bidId, companyId }) => ({
        url: `${BASE_URL}/matching-offers?bidId=${bidId}`,
        headers: {
          'X-Company-Id': companyId,
        },
      }),
      providesTags: (result, error, { bidId }) => [
        {
          type: ApiTags.MARKET_DEPTH,
          id: MarketDepthTagIds.MATCHING_OFFERS,
        },
        {
          type: ApiTags.MARKET_DEPTH,
          id: `${MarketDepthTagIds.MATCHING_OFFERS}-${bidId}`,
        },
      ],
    }),
  }),
});

export const {
  useGetMarketDepthQuery,
  useGetMatchingBidsQuery,
  useGetMatchingOffersQuery,
} = marketDepthApi;
